import Card from 'components/Card';
import InvoiceNominateMonth from 'components/InvoiceNominateMonth';
import Loading from 'components/Loading';
import Reload from 'components/Reload';
import RoundedButton from 'components/RoundedButton';
import { STATUS } from 'constants/invoice';
// eslint-disable-next-line import/no-duplicates
import { PaymentDetails } from 'constants/payment';

// eslint-disable-next-line import/no-duplicates
import { getDate } from 'date-fns';
import { format } from 'date-fns-tz';
// eslint-disable-next-line import/no-duplicates
import pt from 'date-fns/locale/pt-BR';
import { useBottomSheet } from 'hooks/bottomSheet';
import React, { useCallback, useContext, useEffect } from 'react';
import api from 'services/api';
import { nominativeMonthName } from 'services/date';
import { PriceFormat } from 'services/price';
import { ThemeContext } from 'styled-components/native';

import { useNavigation } from '@react-navigation/native';

import { useInvoiceConditionallyWithOrientation } from '../../data/useInvoice';
import BoletoBottonSheet from '../BoletoBottomSheet';
import {
  Container,
  InvoiceDescription,
  InvoiceDescriptionItem,
  InvoiceDescriptionTitle,
  InvoiceDescriptionValue,
  InvoiceHeader,
  InvoiceHeaderButton,
  InvoiceHeaderContainer,
  InvoiceHeaderPrice,
  InvoiceHeaderPriceSymbol,
  InvoiceHeaderPriceText,
  InvoicePayButton,
  InvoicePayButtonText
} from './styles';

interface InvoiceCardInterface {
  title?: string;
  showButton?: boolean;
  horizontalButton?: boolean;
  orientation?: 'RIGHT' | 'LEFT' | null;
}

const InvoiceCard: React.FC<InvoiceCardInterface> = ({
  title,
  showButton = true,
  horizontalButton = false,
  orientation = null,
  ...rest
}) => {
  const {
    invoice: data,
    mutate,
    error,
    loading
  } = useInvoiceConditionallyWithOrientation(orientation);

  const themeContext = useContext(ThemeContext);
  const navigation = useNavigation();
  const { openBottomSheet } = useBottomSheet();

  useEffect(() => {
    if (data) {
      const updatedData = {
        ...data,
        dueDateString: format(new Date(data.dueDate), "dd 'de' MMM yyyy", {
          locale: pt
        }).toLowerCase(),
        valueFormatted: PriceFormat(data.value),
        statusMapped: STATUS[data.statusPayment],
        invoiceMonth: nominativeMonthName(data.dueDate)
      };

      mutate(updatedData, false);
    }
  }, [data]);

  const invoiceIsPayable = data?.paid || Number(data?.value) === 0;
  const invoiceIsNotPayableAndZeroValue =
    horizontalButton && !data?.paid && Number(data?.value) > 0;

  const openBoletoBottonSheet = useCallback(async () => {
    try {
      const { data: paymentDetails } = await api.get<PaymentDetails>(
        'payment/details'
      );
      openBottomSheet(BoletoBottonSheet, { data: paymentDetails });
    } catch (err) {}
  }, []);

  return (
    <Container testID="card-reload" {...rest}>
      <Card title={title} iconName="invoice">
        <Reload
          message="Não foi possível carregar sua fatura"
          onPress={() => mutate()}
          loaded={!!data?.id || !error}
        >
          <InvoiceHeader>
            <InvoiceHeaderContainer>
              <InvoiceNominateMonth data={data} />
              <InvoiceHeaderPrice>
                <InvoiceHeaderPriceSymbol>R$</InvoiceHeaderPriceSymbol>
                <Loading isLoading={loading || error}>
                  <InvoiceHeaderPriceText>
                    {data?.valueFormatted}
                  </InvoiceHeaderPriceText>
                </Loading>
              </InvoiceHeaderPrice>
            </InvoiceHeaderContainer>
            {showButton && (
              <Loading isLoading={loading || error} mask="" showSpinner={false}>
                <InvoiceHeaderButton testID="invoiceHeaderButtonTestId">
                  {!horizontalButton && (
                    <RoundedButton
                      label={invoiceIsPayable ? 'Detalhes' : 'Pagar'}
                      color={
                        invoiceIsPayable
                          ? themeContext?.colors.interaction.slot1
                          : themeContext?.colors.support.slot3
                      }
                      onPress={() => navigation.navigate('InvoiceDetail')}
                    />
                  )}
                  {invoiceIsNotPayableAndZeroValue && (
                    <InvoicePayButton
                      onPress={openBoletoBottonSheet}
                      type={data?.statusMapped === 0 ? 'pay' : 'antecipate'}
                    >
                      <InvoicePayButtonText
                        type={data?.statusMapped === 0 ? 'pay' : 'antecipate'}
                      >
                        {data?.statusMapped === 0 ? 'Pagar' : 'Antecipar'}
                      </InvoicePayButtonText>
                    </InvoicePayButton>
                  )}
                </InvoiceHeaderButton>
              </Loading>
            )}
          </InvoiceHeader>
        </Reload>
        <InvoiceDescription>
          <InvoiceDescriptionItem border>
            <InvoiceDescriptionTitle>Vencimento em</InvoiceDescriptionTitle>
            <Loading isLoading={loading || error} mask="-- -- --">
              <InvoiceDescriptionValue>
                {data?.dueDateString}
              </InvoiceDescriptionValue>
            </Loading>
          </InvoiceDescriptionItem>
          <InvoiceDescriptionItem border>
            <InvoiceDescriptionTitle>Pagamento mínimo</InvoiceDescriptionTitle>
            <Loading isLoading={loading || error} mask="-- -- --">
              <InvoiceDescriptionValue>
                R$ {PriceFormat(data?.minimumValue)}
              </InvoiceDescriptionValue>
            </Loading>
          </InvoiceDescriptionItem>
          <InvoiceDescriptionItem>
            <InvoiceDescriptionTitle>
              Melhor dia para compra
            </InvoiceDescriptionTitle>
            <Loading isLoading={loading || error} mask="-- -- --">
              <InvoiceDescriptionValue>
                Todo dia{' '}
                {data?.bestBuyingDate && getDate(new Date(data.bestBuyingDate))}
              </InvoiceDescriptionValue>
            </Loading>
          </InvoiceDescriptionItem>
        </InvoiceDescription>
      </Card>
    </Container>
  );
};

export default InvoiceCard;
